import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { ReactComponent as UserLogo } from "../../assets/icons/UserLogo.svg";
import { ReactComponent as SearchLogo } from "../../assets/icons/SearchLogo.svg";
import { ReactComponent as EditIconLogo } from "../../assets/icons/EditIconLogo.svg";
import { SCHEDULE_TABLE_HEAD, STATUS_OPTIONS } from "../constants/Constants";
import { convertDate, formatTime } from "../utility/common";
import ScheduleTableStates from "./ScheduleTableStates";
import CustomDropdown from "../InterviewConfigPage/CustomDropdown";
import {
  toggleModalOpen,
  setEditingSchedule,
} from "../../store/schedulingReducer";
import "./Style.css";

const ScheduleTable = ({
  scheduleData = [],
  loading = false,
  error = null,
  searchQuery = "",
  statusFilter = "all",
  onSearchChange,
  onStatusChange,
  scrollRef,
}) => {
  const dispatch = useDispatch();

  const transformedData = useMemo(() => {
    if (!scheduleData || scheduleData.length === 0) {
      return [];
    }

    return scheduleData.map((item, index) => {
      const startTime = formatTime(item?.active_from);
      const endTime = formatTime(item?.active_to);
      const timeRange =
        startTime !== "N/A" && endTime !== "N/A"
          ? `${startTime} - ${endTime}`
          : "N/A";
      
      const scheduleMappingId = item.id || item.mapping_id || item.schedule_id || item.interview_candidate_mapping_id;
      
      return {
        id: scheduleMappingId,
        name: item.student?.name || "N/A",
        email: item.student?.email || "N/A",
        type: item?.interviewConfig?.interview_type || "N/A",
        startDate: convertDate(item?.active_from) || "N/A",
        endDate: convertDate(item?.active_to) || "N/A",
        start: startTime,
        end: endTime,
        timeRange: timeRange,
        durationMin: item?.interviewConfig?.duration || 0,
        status: item.status?.toLowerCase() || "NA",
        interviewId: item?.interview_id,
        originalItem: item,
      };
    });
  }, [scheduleData]);

  const handleEdit = (row) => {
    if (row?.status === "completed" || row?.status === "expired" ) return;
    try {
      const originalItem = row?.originalItem || row;
     
      let scheduleId = row?.id || originalItem?.id || originalItem?.mapping_id || originalItem?.schedule_id || originalItem?.interview_candidate_mapping_id;
      if (!scheduleId) {
        
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const allFields = { ...row, ...originalItem };
        for (const [key, value] of Object.entries(allFields)) {
          if (typeof value === 'string' && uuidPattern.test(value) && key.toLowerCase().includes('id')) {
            scheduleId = value;
            break;
          }
        }
      }
      if (!scheduleId) {
        scheduleId = 'NO_ID_FOUND';
      }
      
      const activeFrom = originalItem?.active_from;
      const activeTo = originalItem?.active_to;

      let startDate = "";
      let startTime = "";
      let endDate = "";
      let endTime = "";

      if (activeFrom) {
        const startDateObj = new Date(activeFrom);
        if (!isNaN(startDateObj.getTime())) {
          startDate = startDateObj.toISOString().split("T")[0];
          const hours = String(startDateObj.getUTCHours()).padStart(2, "0");
          const minutes = String(startDateObj.getUTCMinutes()).padStart(2, "0");
          startTime = `${hours}:${minutes}`;
        }
      }

      if (activeTo) {
        const endDateObj = new Date(activeTo);
        if (!isNaN(endDateObj.getTime())) {
          endDate = endDateObj.toISOString().split("T")[0];
          const hours = String(endDateObj.getUTCHours()).padStart(2, "0");
          const minutes = String(endDateObj.getUTCMinutes()).padStart(2, "0");
          endTime = `${hours}:${minutes}`;
        }
      }

      const formValues = {
        studentName: originalItem?.student?.name || row?.name || "",
        studentEmail: originalItem?.student?.email || row?.email || "",
        studentId: originalItem?.student?.id || "",
        interviewType: originalItem?.interviewConfig?.interview_type || row?.type || "",
        interviewTypeId: originalItem?.interviewConfig?.interview_type_id || originalItem?.interview_id || "",
        form: {
          startDate,
          startTime,
          endDate,
          endTime,
        },
      };

      dispatch(setEditingSchedule({ id: scheduleId, formValues }));
      dispatch(toggleModalOpen(true));
    } catch (error) {
      console.error("Error parsing date in handleEdit:", error);
    }
  };

  const showTableState = loading || error;

  return (
    <div className="schedule-table-container">
      <div className="schedule-table-filters">
        <div className="schedule-table-search-container">
          <SearchLogo />
          <input
            type="text"
            placeholder="Search interviews by name or email or status..."
            className="schedule-table-search-input"
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
          />
        </div>
        <div className="schedule-table-filter-container">
          <CustomDropdown
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={(e) => onStatusChange?.(e.target.value)}
          />
        </div>
      </div>

      <div className="schedule-table-wrapper">
        <div className="schedule-table-scroll custom-scrollbar pr-2" ref={scrollRef}>
          <table className="schedule-table">
            <thead>
              <tr>
                {SCHEDULE_TABLE_HEAD.map((head, index) => (
                  <th key={index}>{head}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={SCHEDULE_TABLE_HEAD.length}
                    className="schedule-table-empty-cell"
                   >
                     <ScheduleTableStates loading={loading} error={null} />
                   </td>
                 </tr>
                ) : error ? (
                 <tr>
                   <td
                     colSpan={SCHEDULE_TABLE_HEAD.length}
                     className="schedule-table-empty-cell"
                   >
                     <ScheduleTableStates loading={false} error={error} />
                   </td>
                 </tr>
                ) : transformedData.length === 0 ? (
                 <tr>
                   <td
                     colSpan={SCHEDULE_TABLE_HEAD.length}
                     className="schedule-table-empty-cell"
                   >
                     <div className="schedule-table-empty-content">
                       <svg
                         className="w-12 h-12 text-gray-300 mb-3"
                         fill="none"
                         stroke="currentColor"
                         viewBox="0 0 24 24"
                       >
                         <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                         />
                       </svg>
            
                       <p className="schedule-table-empty-title">
                          No schedule interviews found
                       </p>
            
                        <p className="schedule-table-empty-text">
                         {searchQuery.trim()
                           ? "Try adjusting your search or filters to see more results."
                           : "Schedule an interview to get started."}
                       </p>
                     </div>
                   </td>
                 </tr>
                ): (
                transformedData.map((row, index) => (
                  <tr key={row.id || index}>
                    <td>
                      <div className="schedule-table-user-cell">
                        <div className="schedule-table-avatar">
                          <UserLogo />
                        </div>
                        <div className="schedule-table-user-info">
                          <div className="schedule-table-user-name">
                            {row?.name}
                          </div>
                          <div className="schedule-table-user-email">
                            {row.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="schedule-table-type">{row.type}</div>
                    </td>
                    <td>
                      <div className="schedule-table-date">{row.startDate}</div>
                      <div className="schedule-table-time">{row.start}</div>
                    </td>
                    <td>
                      <div className="schedule-table-date">{row.endDate}</div>
                      <div className="schedule-table-time">{row.end}</div>
                    </td>
                    <td className="schedule-table-duration">
                      {row.durationMin} min
                    </td>
                    <td>
                      <span
                        className={`schedule-table-status ${
                          row.status === "confirmed"
                            ? "schedule-table-status-confirmed"
                            : row.status === "completed"
                            ? "schedule-table-status-completed"
                            : row.status === "pending"
                            ? "schedule-table-status-pending"
                            : "schedule-table-status-other"
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td>
                      <div className="schedule-table-actions">
                        {(() => {
                          const isEditDisabled =
                            row.status === "completed" ||
                            row.status === "expired";

                          return (
                            <button
                              className={`schedule-table-action-button schedule-table-action-button-green ${
                                isEditDisabled ? "schedule-table-action-button-disabled" : ""
                              }`}
                              onClick={() => !isEditDisabled && handleEdit(row)}
                              disabled={isEditDisabled}
                            >
                              <EditIconLogo className="schedule-table-action-icon" />
                            </button>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ScheduleTable;
